import React, { useEffect, useState, useCallback } from "react";
import styles from './CumulativeClinicalDaySummary.module.scss';
import axiosClient from "../../api/axiosClient";
import { useToasts } from "../../contexts/ToastContext";
import Dropdown from "../dropdown/Dropdown";
import Filter from "../filter/Filter";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import PropTypes from "prop-types";

const CumulativeClinicalDaySummary = ({ studentId }) => {
  const [tabs, setTabs] = useState([]);
  const [selectedTabKey, setSelectedTabKey] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToasts();

  const fetchData = useCallback(async (key, filter) => {
    setIsLoading(true);
    try {
      const params = {};
      if (studentId) params.studentId = studentId;
      if (key && key !== 'all_summary') params.key = key;
      if (filter) params.filter = filter;

      const response = await axiosClient.get('case-log/cumulative-clinical-day-summary', { params });
      const { tabs: apiTabs, totalPatientEncounters, totalPatientCareHours, totalClinicalHours } = response.data;

      if (apiTabs && apiTabs.length > 0) {
        setTabs(apiTabs);
        if (!key) {
          setSelectedTabKey(apiTabs[0].key);
        }
      }

      setStats({ totalPatientEncounters, totalPatientCareHours, totalClinicalHours });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [studentId, showToast]);

  useEffect(() => {
    fetchData(null, null);
  }, [fetchData]);

  const handleTabChange = (key) => {
    setSelectedTabKey(key);
    setSelectedFilter(null);
    fetchData(key, null);
  };

  const handleFilterChange = (value) => {
    const filter = value || null;
    setSelectedFilter(filter);
    fetchData(selectedTabKey, filter);
  };

  const selectedTab = tabs.find(t => t.key === selectedTabKey);
  const filterOptions = selectedTab?.filters
    ? [{ value: '', label: 'All' }, ...selectedTab.filters.map(f => ({ value: String(f), label: String(f) }))]
    : [];

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Cumulative Clinical Day Summary</h2>

      {tabs.length > 0 && (
        <div className={styles.tabsRow}>
          <Filter
            filters={tabs.map(tab => ({ label: tab.title, value: tab.key }))}
            selected={selectedTabKey}
            onChange={handleTabChange}
          />
        </div>
      )}

      <hr className={styles.divider} />

      {selectedTab?.filters?.length > 0 && (
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>{selectedTab.filterByLabel}</span>
          <div className={styles.filterDropdown}>
            <Dropdown
              options={filterOptions}
              value={selectedFilter || ''}
              onChange={handleFilterChange}
              size="small"
            />
          </div>
        </div>
      )}

      {isLoading && <LoadingOverlay />}
      {stats && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={`${styles.dot} ${styles.dotGreen}`} />
              <span className={styles.statLabel}>Total Patient Encounters</span>
            </div>
            <div className={styles.statValue}>{stats.totalPatientEncounters ?? '—'}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={`${styles.dot} ${styles.dotBlue}`} />
              <span className={styles.statLabel}>Total Patient Care Hours</span>
            </div>
            <div className={styles.statValue}>{stats.totalPatientCareHours ?? '—'}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={`${styles.dot} ${styles.dotYellow}`} />
              <span className={styles.statLabel}>Total Clinical Hours</span>
            </div>
            <div className={styles.statValue}>{stats.totalClinicalHours ?? '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

CumulativeClinicalDaySummary.propTypes = {
  studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CumulativeClinicalDaySummary;
